import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CarouselComponent } from 'src/app/layouts/carousel/carousel.component';
import { GetJobOffer } from 'src/app/models/job-offer/get-job-offer';
import { GetJobOfferCandidate } from 'src/app/models/job-offer/get-job-offer-candidate';
import { AuthService } from 'src/app/services/auth.service';
import { JobOfferService } from 'src/app/services/job-offer.service';
import { LoaderService } from 'src/app/services/loader.service';
import { environment } from 'src/environments/environment';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { ApplicationStatusEnum, ApplicationStatusUtils } from 'src/app/enums/application-status.enum';
import { JobApplicationService } from 'src/app/services/job-application.service';
import { ViewType, ViewTrackingService } from 'src/app/services/view-tracking.service';
import { ViewCountBaseService } from 'src/app/services/view-count-base.service';

@Component({
  selector: 'app-job-offer-list',
  imports: [CommonModule, RouterLink, FormsModule, PaginationComponent],
  templateUrl: './job-offer-list.component.html',
  styleUrl: './job-offer-list.component.css'
})
export class JobOfferListComponent implements OnInit {
  appliedOffers = input<boolean>(false);
  jobOffers = signal<GetJobOfferCandidate[]>([]);
  page = 1;
  pageSize = 5;
  totalItems = 0;
  totalPages = 0;
  isLoading = signal(false);
  thereAreNoJobOffers = signal(false);
  isUserLoggedIn = signal(false);

  // Tracking properties
  ViewType = ViewType;
  viewCounts = signal<{ [key: number]: number }>({});

  url = environment.api.url;

  constructor(
    private jobOfferService: JobOfferService,
    private authService: AuthService,
    private docService: FileStorageService,
    private jobApplicationService: JobApplicationService,
    private viewTrackingService: ViewTrackingService,
    private viewCountService: ViewCountBaseService
  ) {
    this.isUserLoggedIn.set(this.authService.isValidToken());
  }

  ngOnInit(): void {
    this.getJobOffers();
  }

  getJobOffers() {
    this.isLoading.set(true);
    this.jobOfferService.getJobOffersCandidate({
      page: this.page,
      pageSize: this.pageSize,
      appliedOffers: this.appliedOffers()
    }).subscribe({
      next: res => {
        // Mapear las ofertas laborales y añadir información del estado de la aplicación
        const jobOffers = res?.items.map((x) => {
          // Asignar un estado de aplicación por defecto para ofertas aplicadas
          const offer: GetJobOfferCandidate = {
            ...x,
            logoUrl: x.logoFileId ? this.docService.getFileUrl(`${x.logoFileId}`) : null
          };
          
          // Si es una oferta aplicada, asignar un estado por defecto (Pendiente)
          if (this.appliedOffers() && x.appliedOffer) {
            const statusId = offer.jobApplicationStatusId;
            if (statusId) {
              offer.jobApplicationStatusName = ApplicationStatusUtils.getStatusName(statusId);
            }
          }
          
          return offer;
        }) ?? [];
        
        this.thereAreNoJobOffers.set(jobOffers.length === 0);
        this.isLoading.set(false);
        this.totalItems = res?.totalItems ?? jobOffers.length;
        this.jobOffers.set(jobOffers);
        this.totalPages = res?.totalPages ?? 0;
        
        // Cargar conteos de vistas después de obtener las ofertas
        this.loadViewCounts();
      },
      error: () => {
        this.thereAreNoJobOffers.set(true);
        this.isLoading.set(false);
      }
    });
  }
  
  // Método para obtener el color del badge según el estado de la aplicación
  getStatusBadgeClass(statusId?: ApplicationStatusEnum): string {
    if (!statusId) return 'bg-secondary';
    
    switch (statusId) {
      case ApplicationStatusEnum.Pendiente:
        return 'bg-warning text-dark';
      case ApplicationStatusEnum.Entrevista:
        return 'bg-info text-dark';
      case ApplicationStatusEnum.Aprobado:
        return 'bg-success';
      case ApplicationStatusEnum.Rechazado:
        return 'bg-danger';
      case ApplicationStatusEnum.Contratado:
        return 'bg-primary';
      case ApplicationStatusEnum.Otro:
      default:
        return 'bg-secondary';
    }
  }
  

  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
      if (this.jobOffers().length < this.totalItems) {
        this.getJobOffers();
      }
    }
  }

  onPageChange(page: number) {
    if (page > 0 && page <= this.totalPages) {
      this.page = page;
      this.getJobOffers();
    }
  }

  getViewCount(jobOfferId: number): number {
    return this.viewCountService.getViewCount(this.viewCounts, jobOfferId);
  }

  loadViewCounts() {
    this.viewCountService.loadViewCounts(this.jobOffers(), ViewType.JobOffer, this.viewCounts);
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.page = 1;
    this.getJobOffers();
  }
}
